import React from 'react';

import { ApiContext } from './apicontext';
import { useStateWithStorage } from '../utils/storage';
import { IApiResult, IUserData, IUserFetchResult } from '../model/api';

export interface ISessionContext {
	session: string;
	setSession: (session: string) => void;
	user: IUserData | undefined;
	setUser: (user: IUserData | undefined) => void;
}

const defaultSession: ISessionContext = {
	session: '',
	setSession: () => {},
	user: undefined,
	setUser: () => {}
};

export const SessionContext = React.createContext<ISessionContext>(defaultSession);

type SessionProviderProps = {
	children: React.ReactNode;
};

export const SessionProvider = (props: SessionProviderProps) => {
	const Api = React.useContext(ApiContext);
	const { children } = props;

	const [initialized, setInitialized] = React.useState<boolean>(false);
	const [session, setSession] = useStateWithStorage<string>(
		'session',
		'',
		{
			rememberForever: true,
			onInitialize: () => setInitialized(true)
		}
	);
	const [user, setUser] = useStateWithStorage<IUserData | undefined>('user', undefined);

	React.useEffect(() => {
		// Listen for session changes from other tabs
		window.addEventListener('storage', handleStorageEvent);
		// Cleanup
		return () => {
			window.removeEventListener('storage', handleStorageEvent);
		};

		function handleStorageEvent(event: StorageEvent): void {
			if (event.key === 'session') {
				// Log out
				if (event.oldValue !== '' && !event.newValue) {
					setSession('');
				}
				// Session changed (e.g. log in)
				else if (event.newValue) {
					setSession(JSON.parse(event.newValue));
				}
			}
		}
	}, [setSession]);

	React.useEffect(() => {
		if (!initialized) return;
		if (session !== '' && !user) {
			const headers = {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${session}`
			};
			Api.fetch('user/public',
				{ headers },
				(result: IApiResult) => {
					if (result.response === 1)
						setUser((result as IUserFetchResult).user);
					// Response == 0: No valid session match => Clear session
					else if (result.response === 0)
						setSession('');
					// Response == -1: API unavailable. Do not clear session
				}
			);
		}
		// Clear user if at this point there's somehow a user without a session
		else if (session === '' && user) {
			setUser(undefined);
		}
	}, [initialized, session]);

	const providerValue: ISessionContext = { session, setSession, user, setUser };

	return (
		<SessionContext.Provider value={providerValue}>
			{children}
		</SessionContext.Provider>
	);
};
